import { ToastNotificationTypes } from "@sscs/types";
import { Toast } from "@sscs/ui";
import { compareValues } from "@sscs/utils";
import {
  Package,
  PackageMessage,
  PackageSummary,
  PredecessorOptions,
  Project,
  ProjectChange,
  ProjectMessage,
  ProjectSummary,
  Version,
} from "Types";

export function sortProjects<T extends { modified_on: string }>(
  projects: T[],
): T[] {
  return [...projects].sort((first, second) =>
    compareValues(first.modified_on, second.modified_on, false),
  );
}

export const sortVersions = (versions: Version[]): Version[] => {
  return [...versions].sort((first, second) =>
    compareValues(first.created, second.created, true),
  );
};

export const versionsToPredecessors = (
  versions: Version[],
  deletedVersions: ProjectChange[],
): PredecessorOptions => {
  return versions
    .filter(
      (version) =>
        !deletedVersions.some(
          (deletedVersion) => deletedVersion.id === version.version_id,
        ),
    )
    .map((version) => ({
      id: version.version_id,
      createdDate: version.created,
      predecessorId: version.predecessor_id,
      softwareInfo: version.software_info,
    }));
};

const UNKNOWN_USER = "unknown user";

const COMMON_NOTIFICATION_ARGS: ToastNotificationTypes = {
  title: "",
  type: "info",
  props: {
    autoClose: false,
  },
};

export const createProjectsNotification = (
  message: ProjectMessage,
  projects: { name: string }[],
) => {
  return Toast.show({
    ...COMMON_NOTIFICATION_ARGS,
    title: `Project ${projects
      .map((project) => project.name)
      .join(", ")} has been ${message.action} by ${
      message.initiated_by ?? UNKNOWN_USER
    }`,
  });
};

export const createPackageNotification = (
  message: PackageMessage,
  packages: Omit<PackageSummary, "analysis_overview">[],
) => {
  return Toast.show({
    ...COMMON_NOTIFICATION_ARGS,
    title: `Package ${packages.map((item) => item.name).join(", ")} has been ${
      message.action
    } by ${message.initiated_by ?? UNKNOWN_USER}`,
  });
};

export const convertToPackageSummary = (
  packageData: Package,
): PackageSummary => {
  const finalCheck =
    packageData.analysis_overview?.final_scan_report.checks.find(
      (check) => check.final,
    );

  return {
    ...packageData,
    analysis_overview: {
      final_check_label: finalCheck?.label ?? null,
      inhibitors:
        packageData.analysis_overview?.final_scan_report.inhibitors ?? null,
    },
  };
};

export const convertToProjectSummary = (project: Project): ProjectSummary => {
  return {
    ...project,
    packages: project.packages.map((item) => convertToPackageSummary(item)),
  };
};
